import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ExternalLink,
  Loader2,
  PenLine,
  Ticket,
  Trash2,
} from "lucide-react";
import { selectAppUser } from "../../redux/appAuthSlice";
import {
  deleteJoinedBet,
  editJoinedBetStake,
} from "../../redux/joinedBetsSlice";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  formatTicketDate,
  getBetHouseName,
  getTicketProgress,
  isTicketEditable,
  normalizeTicketStatus,
} from "../../utils/joinedBetsHelpers";
import JoinedBetEditModal from "./JoinedBetEditModal";
import JoinedBetLegTable from "./JoinedBetLegTable";

const houseLink = (bet) => {
  const id = bet?.bet?._id || bet?.betId || bet?.bet;
  if (!id) return null;
  return `/app/bethouse/${id}`;
};

const JoinedBetInspector = ({ bet, onToast, onDeleted, statusBar }) => {
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;
  const userCash = Number(useSelector(selectAppUser)?.CASH ?? 0);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fixtures = useMemo(
    () => (Array.isArray(bet?.fixtures) ? bet.fixtures : []),
    [bet?.fixtures]
  );
  const progress = useMemo(() => getTicketProgress(fixtures), [fixtures]);

  if (!bet) {
    return (
      <div className="jb-inspector">
        <div className="jb-inspector-placeholder">
          <Ticket size={40} strokeWidth={1.25} />
          <h2>Select a ticket</h2>
          <p>
            Choose a slip from the list to see every leg, market, odds, and live
            fixture status in one view.
          </p>
        </div>
        {statusBar}
      </div>
    );
  }

  const statusKey = normalizeTicketStatus(bet.status);
  const isEditable = isTicketEditable(statusKey);
  const publicPath = houseLink(bet);

  const handleEdit = async (additionalAmount) => {
    if (!userId) return;
    setBusy(true);
    try {
      await dispatch(
        editJoinedBetStake({ userId, bettingId: bet._id, additionalAmount })
      ).unwrap();
      onToast?.("success", "Updated", "Stake updated.");
      setEditOpen(false);
    } catch (err) {
      onToast?.("error", "Error", err || "Could not update stake.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await dispatch(deleteJoinedBet({ userId, bettingId: bet._id })).unwrap();
      onToast?.("success", "Removed", "Ticket deleted.");
      setDeleteOpen(false);
      onDeleted?.();
    } catch (err) {
      onToast?.("error", "Error", err || "Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="jb-inspector">
        <header className="jb-inspector-head">
          <div className="jb-inspector-title">
            <p className="jb-kicker">Ticket inspector</p>
            <h2>{getBetHouseName(bet)}</h2>
          </div>

          <div className="jb-inspector-head-right">
            <div className="jb-inspector-meta">
              <time>{formatTicketDate(bet.createdAt)}</time>
              {publicPath ? (
                <Link to={publicPath} className="jb-inspector-house-link">
                  View house
                  <ExternalLink size={12} />
                </Link>
              ) : null}
            </div>
            {isEditable ? (
              <div className="jb-inspector-actions">
                <button
                  type="button"
                  className="jb-action-btn"
                  disabled={busy}
                  onClick={() => setEditOpen(true)}
                >
                  <PenLine size={14} />
                  Add stake
                </button>
                <button
                  type="button"
                  className="jb-action-btn jb-action-btn-danger"
                  disabled={busy}
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        </header>

        {statusBar}

        <div className="jb-inspector-stats">
          <div className="jb-inspector-stat">
            <span>Staked</span>
            <strong className="tabular-nums">
              {formatCash(bet.amountBet || 0, { compact: true })}
            </strong>
          </div>
          <div className="jb-inspector-stat">
            <span>Potential return</span>
            <strong className="tabular-nums jb-stat-accent">
              {formatCash(bet.potentialWinnings || 0, { compact: true })}
            </strong>
          </div>
          <div className="jb-inspector-stat">
            <span>Legs</span>
            <strong className="tabular-nums">{fixtures.length}</strong>
          </div>
          <div className="jb-inspector-stat jb-inspector-stat-progress">
            <div className="jb-inspector-stat-progress-copy">
              <span>Fixture progress</span>
              <strong className="tabular-nums">
                {progress.finished}/{progress.total}
              </strong>
            </div>
            <div className="jb-progress-track jb-progress-track-lg">
              <span style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
            </div>
          </div>
        </div>

        <div className="jb-inspector-body">
          <JoinedBetLegTable fixtures={fixtures} />
        </div>
      </div>

      <JoinedBetEditModal
        open={editOpen}
        bet={bet}
        userCash={userCash}
        busy={busy}
        onClose={() => !busy && setEditOpen(false)}
        onSubmit={handleEdit}
      />

      {deleteOpen ? (
        <div className="jb-modal-root" role="dialog" aria-modal="true">
          <button
            type="button"
            className="jb-modal-backdrop"
            aria-label="Close"
            disabled={busy}
            onClick={() => setDeleteOpen(false)}
          />
          <div className="jb-modal-sheet jb-modal-sheet-danger">
            <p className="jb-modal-eyebrow">Delete ticket</p>
            <h2 className="jb-modal-title">Delete this slip?</h2>
            <p className="jb-modal-sub">
              This removes your stake from the house board. You cannot undo it.
            </p>
            <div className="jb-modal-actions">
              <button
                type="button"
                className="jb-modal-cancel"
                disabled={busy}
                onClick={() => setDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="jb-modal-confirm jb-modal-confirm-danger"
                disabled={busy}
                onClick={handleDelete}
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Deleting…
                  </>
                ) : (
                  "Delete ticket"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default JoinedBetInspector;
